import type { Project } from '@ai-studio/shared';
import { useCallback, useEffect, useState } from 'react';

import { ApiError, api } from '../../lib/api.js';
import { useProjectTabs } from '../../state/project-tabs.js';
import { defaultSetup, NewGameOptions, type GameSetup } from '../NewGameOptions.js';
import { Invitations } from '../social/Invitations.js';
import { ShareDialog } from '../social/ShareDialog.js';
import { signInUrl, type SiteUser } from './WebsiteApp.js';

/** An account's projects: create one from a template, open it in the IDE, delete it. */
export function ProjectsPage({ user, ready }: { user: SiteUser | null; ready: boolean }) {
  const [projects, setProjects] = useState<Project[] | null>(null);
  /** Projects other accounts invited this one into (accepted). */
  const [shared, setShared] = useState<Project[]>([]);
  const [sharing, setSharing] = useState<Project | null>(null);
  const [name, setName] = useState('');
  const [setup, setSetup] = useState<GameSetup>(() => defaultSetup('3d'));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const open = useProjectTabs((s) => s.open);
  const reconcile = useProjectTabs((s) => s.reconcile);

  const load = useCallback(async () => {
    try {
      const r = await api.get<{ projects: Project[]; shared?: Project[] }>('/projects');
      setProjects(r.projects);
      setShared(r.shared ?? []);
      // Tabs of projects you no longer have (deleted, or you left) close; shared ones stay.
      reconcile([...r.projects, ...(r.shared ?? [])]);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load your projects.');
    }
  }, [reconcile]);

  useEffect(() => {
    if (user) void load();
  }, [user, load]);

  // `?open=<id>`: the desktop app's link to a project (its own or shared with it) opens it here.
  useEffect(() => {
    if (!projects) return;
    const params = new URLSearchParams(window.location.search);
    const want = Number(params.get('open'));
    if (!want) return;
    const found = [...projects, ...shared].find((p) => p.id === want);
    if (found) open(found.id, found.name);
    params.delete('open');
    const rest = params.toString();
    window.history.replaceState(null, '', `${window.location.pathname}${rest ? `?${rest}` : ''}`);
  }, [projects, shared, open]);

  if (!ready) return null;
  if (!user)
    return (
      <div className="site-page">
        <h1>My projects</h1>
        <p className="muted">Sign in to make games in your browser with Prism Engine Cloud.</p>
        <a className="primary button-link" href={signInUrl()}>
          Sign in with Google
        </a>
      </div>
    );

  async function create(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ project: Project }>('/projects', { name, template: setup.template, brief: setup.brief || undefined, createIfMissing: true });
      setName('');
      await load();
      if (r.project) open(r.project.id, r.project.name);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create the project.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(project: Project) {
    if (!window.confirm(`Delete "${project.name}" and all its files? Published versions in the library stay until you delete them under Account.`)) return;
    try {
      useProjectTabs.getState().remove(project.id);
      await api.del(`/projects/${project.id}`);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not delete the project.');
    }
  }

  async function leave(project: Project) {
    if (!window.confirm(`Leave "${project.name}"? You will need a new invitation to come back.`)) return;
    try {
      useProjectTabs.getState().remove(project.id);
      await api.del(`/projects/${project.id}/collaborators/${user!.id}`);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not leave the project.');
    }
  }

  return (
    <div className="site-page projects-page">
      <Invitations onAccepted={() => void load()} />
      <h1>My projects</h1>
      {error && (
        <p className="login-error" role="alert">
          {error}
        </p>
      )}
      <div className="projects-layout">
        <section>
          {projects === null ? (
            <p className="muted">Loading…</p>
          ) : projects.length === 0 ? (
            <p className="muted">No projects yet. Start one on the right.</p>
          ) : (
            <ul className="site-list">
              {projects.map((p) => (
                <li key={p.id}>
                  <button type="button" className="site-list-main" onClick={() => open(p.id, p.name)}>
                    <strong>{p.name}</strong>
                    {p.description && <span className="muted">{p.description}</span>}
                    <span className="muted">Edited {new Date(p.updatedAt).toLocaleString()}</span>
                  </button>
                  <button type="button" className="ghost" onClick={() => setSharing(p)}>
                    Share
                  </button>
                  <button type="button" className="ghost danger" onClick={() => void remove(p)}>
                    Delete
                  </button>
                </li>
              ))}
            </ul>
          )}

          <h2 className="soc-section-title">Invited projects</h2>
          {shared.length === 0 ? (
            <p className="muted">Projects other people invite you to work on appear here, apart from your own.</p>
          ) : (
            <ul className="site-list">
              {shared.map((p) => (
                <li key={p.id}>
                  <button type="button" className="site-list-main" onClick={() => open(p.id, p.name)}>
                    <strong>{p.name}</strong>
                    <span className="muted soc-owner">Invited by {p.ownerName ?? 'its owner'}</span>
                    <span className="muted">Edited {new Date(p.updatedAt).toLocaleString()}</span>
                  </button>
                  <button type="button" className="ghost" onClick={() => setSharing(p)}>
                    People
                  </button>
                  <button type="button" className="ghost danger" onClick={() => void leave(p)}>
                    Leave
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
        <form className="site-card" onSubmit={create}>
          <h2>New game</h2>
          <NewGameOptions setup={setup} onChange={setSetup} />
          <label className="field">
            <span>Name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="My Game" required maxLength={80} />
          </label>
          <button className="primary" type="submit" disabled={busy || !name.trim()}>
            {busy ? 'Creating…' : 'Create and open'}
          </button>
        </form>
      </div>
      {sharing && <ShareDialog projectId={sharing.id} projectName={sharing.name} onClose={() => (setSharing(null), void load())} />}
    </div>
  );
}
